import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// UI
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import RefreshIcon from '@mui/icons-material/Refresh';
import RotateRightIcon from '@mui/icons-material/RotateRight';

import { GameStatus } from '../../constants/gameConstants';
import { getEvent } from '../../features/event/eventSlice';
import { getGameByEventId } from '../../features/game/gameSlice';
import gameService from '../../features/game/gameService';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderRight: 'none'
  }
}));

const ClickableTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px'
  }
}));

const STATUS_OPTIONS = [
  GameStatus.NOT_STARTED,
  GameStatus.IN_PROGRESS,
  GameStatus.COMPLETE,
  GameStatus.PLAYOFF
];

const STATUS_META = {
  [GameStatus.NOT_STARTED]: { label: 'Not started', color: 'default' },
  [GameStatus.IN_PROGRESS]: { label: 'In progress', color: 'info' },
  [GameStatus.COMPLETE]: { label: 'Complete', color: 'success' },
  [GameStatus.PLAYOFF]: { label: 'Playoff', color: 'warning' }
};

// How often the list may re-fetch on its own. The value doubles as the interval in
// milliseconds, so 0 means 'Off' and the timer effect can simply skip it.
const AUTO_REFRESH_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 30 * 1000, label: '30 sec' },
  { value: 60 * 1000, label: '1 min' },
  { value: 5 * 60 * 1000, label: '5 min' },
  { value: 15 * 60 * 1000, label: '15 min' },
  { value: 30 * 60 * 1000, label: '30 min' }
];

const AUTO_REFRESH_STORAGE_KEY = 'bowlscout.allGames.autoRefreshMs';

// Filters belong to one event and to one tab, so they go in sessionStorage: the view is
// remembered for the visit without outliving the tab or leaking into another event's page.
const FILTERS_STORAGE_PREFIX = 'bowlscout.allGames.filters.';

const getFiltersStorageKey = (eventId) => `${FILTERS_STORAGE_PREFIX}${eventId}`;

// Every filter starts out empty, and anything unreadable in storage falls back to this shape.
const EMPTY_FILTERS = { name: '', round: '', green: '', status: '' };

// The sort shares the filters' entry, because it belongs to the same remembered view, so it needs
// a default of its own: '' is the draw order the page shows before any heading has been clicked,
// and the direction only matters once a key has been chosen.
const DEFAULT_SORT = { sortKey: '', sortDirection: 'asc' };

// The headings that can sort the list. A remembered key that names anything else is ignored
// rather than trusted, which is what keeps a stale or hand-edited blob harmless.
const SORT_KEYS = [
  'section',
  'round',
  'greenRink',
  'teamA',
  'teamB',
  'status',
  'lastUpdate',
  'photo'
];

// The four filters are stored as strings, so each field can be checked for the shape it should
// have and anything else — a corrupt string, an array, a number, a missing key — is ignored. The
// sort rides in the same entry and is checked just as hard. Both reading and writing stay
// defensive: storage can be unavailable (private mode, blocked storage), and none of that may
// keep the page from rendering.
const readStoredFilters = (eventId) => {
  const fallback = { ...EMPTY_FILTERS, ...DEFAULT_SORT };
  if (!eventId) return fallback;

  try {
    const stored = sessionStorage.getItem(getFiltersStorageKey(eventId));
    if (stored == null) return fallback;

    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return fallback;

    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      round: typeof parsed.round === 'string' ? parsed.round : '',
      green: typeof parsed.green === 'string' ? parsed.green : '',
      status: typeof parsed.status === 'string' ? parsed.status : '',
      // A blob written before the sort existed carries neither field, which lands on the defaults
      // above; a direction that is not exactly 'desc' is as good as missing, since 'asc' is the
      // only other value this page ever writes.
      sortKey: SORT_KEYS.includes(parsed.sortKey) ? parsed.sortKey : DEFAULT_SORT.sortKey,
      sortDirection: parsed.sortDirection === 'desc' ? 'desc' : DEFAULT_SORT.sortDirection
    };
  } catch {
    // No storage access, or a value JSON.parse could not read: nothing was remembered.
    return fallback;
  }
};

// The status filter holds a numeric GameStatus here, so a remembered status only counts once it
// names an option this page offers; anything else means 'no status filter'.
const toStatusFilter = (status) =>
  STATUS_OPTIONS.find((option) => String(option) === status) ?? '';

// Games store the status either as the numeric GameStatus value or as its string name.
// Stripping everything but letters lets hyphenated and lowercase variants such as
// 'in-progress' or 'not_started' all land on the same value.
const normalizeStatus = (status) => {
  const numeric = Number(status);
  if (Number.isFinite(numeric) && STATUS_META[numeric]) {
    return numeric;
  }

  switch (String(status).toUpperCase().replace(/[^A-Z]/g, '')) {
    case 'NOTSTARTED':
      return GameStatus.NOT_STARTED;
    case 'INPROGRESS':
      return GameStatus.IN_PROGRESS;
    case 'COMPLETE':
      return GameStatus.COMPLETE;
    case 'PLAYOFF':
      return GameStatus.PLAYOFF;
    default:
      return null;
  }
};

const getPlayerNames = (team) =>
  (Array.isArray(team?.players) ? team.players : [])
    .map((player) => (typeof player === 'string' ? player : player?.name))
    .filter(Boolean);

const getTeamLabel = (team) => {
  if (team?.name) return team.name;

  const players = getPlayerNames(team);
  return players.length > 0 ? players.join(', ') : '—';
};

const getGreenRinkLabel = (game) => {
  const parts = [];
  if (game?.green != null && game.green !== '') parts.push(`Green ${game.green}`);
  if (game?.rink != null && game.rink !== '') parts.push(`Rink ${game.rink}`);
  return parts.length > 0 ? parts.join(' · ') : '—';
};

// A game in the draw has no name of its own, so fall back to the two teams.
const getGameLabel = (game) => {
  if (game?.name) return game.name;

  const teamA = getTeamLabel(game?.teams?.teamA);
  const teamB = getTeamLabel(game?.teams?.teamB);
  return teamA === '—' && teamB === '—' ? 'Game' : `${teamA} vs ${teamB}`;
};

// updatedAt arrives as a Firestore Timestamp serialised to JSON ({ _seconds, _nanoseconds }),
// but it can also be a live Timestamp, a Date, an ISO string or a number. Anything we cannot
// turn into a date reports 0 so callers can show a blank instead of 'Invalid Date'.
const getUpdatedAtMs = (value) => {
  if (!value) {
    return 0;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate().getTime();
  }

  if (typeof value?._seconds === 'number') {
    return value._seconds * 1000 + Math.round((value._nanoseconds || 0) / 1e6);
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

// Compact age label for the table, e.g. 'just now', '5 min ago', '3 h ago'. Old enough
// updates read better as a plain date than as a big number of days.
const formatRelativeTime = (ms, now) => {
  if (!ms) return '—';

  const elapsed = Math.max(0, now - ms);

  if (elapsed < 60 * 1000) return 'just now';
  if (elapsed < 60 * 60 * 1000) return `${Math.floor(elapsed / (60 * 1000))} min ago`;
  if (elapsed < 24 * 60 * 60 * 1000) return `${Math.floor(elapsed / (60 * 60 * 1000))} h ago`;
  if (elapsed < 7 * 24 * 60 * 60 * 1000) {
    return `${Math.floor(elapsed / (24 * 60 * 60 * 1000))} d ago`;
  }

  return new Date(ms).toLocaleDateString();
};

const formatExactTime = (ms) => (ms ? new Date(ms).toLocaleString() : '');

// The list is loaded without photos, so a row carries only the 'has a photo' flag. The full game
// — and any older response still in the store — carries the photo itself, so read either. The
// base64 string is never rendered from here; the viewer fetches the one photo it shows.
const hasPhoto = (game) => Boolean(game?.hasScorecardPhoto ?? game?.scorecardPhoto);

// Turning the photo re-encodes it with a canvas, so the canvas has to be told what the photo
// actually is: the mime type is read from the data URL itself, and anything unreadable is
// treated as a JPEG.
const getPhotoMimeType = (src) => {
  const match = /^data:([^;,]+)/.exec(typeof src === 'string' ? src : '');
  return match ? match[1] : 'image/jpeg';
};

const getPhotoExtension = (src) => (getPhotoMimeType(src) === 'image/png' ? 'png' : 'jpg');

// A downloaded name has to survive every filesystem, so anything outside letters, numbers, spaces,
// dashes and underscores is dropped and runs of whitespace collapse to a single space.
const getPhotoFileName = (game, src) => {
  const label = getGameLabel(game)
    .replace(/[^A-Za-z0-9 _-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return `${label || 'Game'} scorecard.${getPhotoExtension(src)}`;
};

// Draw the original photo onto a canvas turned by `rotation` degrees and hand back the result as a
// data URL. Resolves null when the photo cannot be drawn, so a turn that fails leaves the viewer on
// the photo it was already showing rather than breaking the dialog.
const rotatePhoto = (src, rotation) =>
  new Promise((resolve) => {
    if (!src || rotation % 360 === 0) {
      resolve(null);
      return;
    }

    const image = new Image();

    image.onload = () => {
      try {
        // A quarter turn swaps the sides, so a landscape photo gets a portrait canvas instead of
        // being clipped to its old shape.
        const quarterTurn = rotation % 180 !== 0;
        const canvas = document.createElement('canvas');
        canvas.width = quarterTurn ? image.naturalHeight : image.naturalWidth;
        canvas.height = quarterTurn ? image.naturalWidth : image.naturalHeight;

        const context = canvas.getContext('2d');
        if (!context || canvas.width === 0 || canvas.height === 0) {
          resolve(null);
          return;
        }

        // Turn about the centre of the canvas, then draw the photo centred on that same point.
        context.translate(canvas.width / 2, canvas.height / 2);
        context.rotate((rotation * Math.PI) / 180);
        context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

        resolve(canvas.toDataURL(getPhotoMimeType(src), 0.95));
      } catch {
        // No 2d context, or a canvas that refuses to encode: keep the photo as it came.
        resolve(null);
      }
    };

    image.onerror = () => resolve(null);
    image.src = src;
  });

// Values may be numbers (rounds, rinks) or codes (greens), so sort numerically where
// possible and fall back to a natural string comparison. Blank values sort last.
const compareValues = (a, b) => {
  const missingA = a == null || a === '';
  const missingB = b == null || b === '';

  if (missingA || missingB) {
    if (missingA && missingB) return 0;
    return missingA ? 1 : -1;
  }

  const numericA = Number(a);
  const numericB = Number(b);
  if (Number.isFinite(numericA) && Number.isFinite(numericB)) {
    return numericA - numericB;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

// One key's comparison, already turned the way the column runs. The blank check happens before
// the direction is applied, so a missing value stays at the bottom of the table whichever way the
// column runs: flipping a 'blank last' result would lift every missing value to the top instead.
const compareForKey = (a, b, direction) => {
  const missingA = a == null || a === '';
  const missingB = b == null || b === '';

  if (missingA || missingB) {
    if (missingA && missingB) return 0;
    return missingA ? 1 : -1;
  }

  const diff = compareValues(a, b);
  return direction === 'desc' ? -diff : diff;
};

// The value — or the chain of values, for a column that breaks ties — a game sorts on. Anything
// the game does not have comes back as null or undefined, which the comparison reads as blank; a
// chain is walked in order, so the first part that decides the order wins.
const getSortValues = (game, sortKey, sectionMeta) => {
  switch (sortKey) {
    case 'section': {
      // A section the event no longer lists has no draw order to sort by, so its raw id — the same
      // thing the cell shows — stands in for the name.
      const section = sectionMeta[game?.section];
      if (!section) return [game?.section];

      return [section.order ?? section.name];
    }
    case 'round':
      return [game?.round];
    case 'greenRink':
      // Green decides the order first, and the rinks sharing a green follow.
      return [game?.green, game?.rink];
    case 'teamA':
      return [getTeamLabel(game?.teams?.teamA).toLowerCase()];
    case 'teamB':
      return [getTeamLabel(game?.teams?.teamB).toLowerCase()];
    case 'status':
      // An unrecognisable status reports null, which reads as blank and so sorts after every known
      // status in both directions.
      return [normalizeStatus(game?.status)];
    case 'lastUpdate':
      // 0 is how getUpdatedAtMs reports a missing or unreadable date, and such a row already shows
      // '—' in the table, so it counts as blank rather than as the oldest possible update.
      return [getUpdatedAtMs(game?.updatedAt) || undefined];
    case 'photo':
      // Ranked rather than boolean so the games that have a photo come first when ascending. A
      // game without one is not missing anything, it simply ranks after the ones that have it.
      return [hasPhoto(game) ? 0 : 1];
    default:
      return [];
  }
};

const getDistinctValues = (games, key) => {
  const values = new Map();
  games.forEach((game) => {
    const value = game?.[key];
    if (value == null || value === '') return;
    values.set(String(value), value);
  });
  return Array.from(values.values()).sort(compareValues);
};

const EventAllGames = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  const event = useSelector((state) => state.event.event);
  const games = useSelector((state) => state.game.games);
  const loadingEvent = useSelector((state) => state.event.loading);

  // The games slice does not track this request, so the page keeps its own loading flag.
  // It starts true so the first paint shows the spinner rather than the empty state.
  const [loadingRequest, setLoadingRequest] = useState(true);

  // Read what this event was last left filtered and sorted by, once per event and during render.
  // Seeding the state below straight from it is what keeps the persist effect from firing (and
  // overwriting the remembered filters with the empty defaults) before the value has ever been
  // read: a useState initialiser only runs on the first render, by which point the value is
  // already in hand.
  const storedFilters = useMemo(() => readStoredFilters(eventId), [eventId]);

  const [filterName, setFilterName] = useState(storedFilters.name);
  const [filterRound, setFilterRound] = useState(storedFilters.round);
  const [filterGreen, setFilterGreen] = useState(storedFilters.green);
  // The status filter holds a GameStatus number, so the remembered string is mapped back to the
  // option it names; the other three compare as strings either way.
  const [filterStatus, setFilterStatus] = useState(toStatusFilter(storedFilters.status));

  // The column the list is sorted by and which way it runs. '' is not a column: it leaves the list
  // in the draw order the games arrive in.
  const [sortKey, setSortKey] = useState(storedFilters.sortKey);
  const [sortDirection, setSortDirection] = useState(storedFilters.sortDirection);

  // The page can stay mounted while the route moves to another event, and the filters and the sort
  // belong to one event only. Adjusting them during the render that sees the new event id, rather
  // than in an effect after it, leaves no window in which the persist effect below could show or
  // store the previous event's view under the new event's key.
  const [filtersEventId, setFiltersEventId] = useState(eventId);

  if (filtersEventId !== eventId) {
    setFiltersEventId(eventId);
    setFilterName(storedFilters.name);
    setFilterRound(storedFilters.round);
    setFilterGreen(storedFilters.green);
    setFilterStatus(toStatusFilter(storedFilters.status));
    setSortKey(storedFilters.sortKey);
    setSortDirection(storedFilters.sortDirection);
  }

  // Remember the filters and the sort for this event, so opening a game and coming back lands on
  // the same view. This also writes on the first render, but the state already holds the stored
  // values by then, so that write repeats them instead of replacing them with the empty defaults.
  // Writing is just as cautious as reading: storage may be unavailable, which never stops the
  // filters or the sort applying for this visit.
  useEffect(() => {
    if (!eventId) return;

    try {
      sessionStorage.setItem(
        getFiltersStorageKey(eventId),
        JSON.stringify({
          name: filterName,
          round: String(filterRound),
          green: String(filterGreen),
          status: String(filterStatus),
          sortKey,
          sortDirection
        })
      );
    } catch {
      // Storage is unavailable: the filters and the sort still apply for this visit.
    }
  }, [eventId, filterName, filterRound, filterGreen, filterStatus, sortKey, sortDirection]);

  // The auto-refresh tick needs to know whether a request is already in flight, but reading the
  // state directly inside the callback would force the timer effect to depend on it and rebuild
  // the interval on every load. This ref mirrors the state so the callback stays current without
  // that dependency.
  const loadingRef = useRef(loadingRequest);

  useEffect(() => {
    loadingRef.current = loadingRequest;
  }, [loadingRequest]);

  // The game whose scorecard photo is open in the viewer, or null while it is closed. The list
  // arrives without photos, so the dialog fetches the one game it shows; the photo itself is a
  // base64 data URL that lives in state only while the dialog is open.
  const [photoGame, setPhotoGame] = useState(null);
  const [photoSrc, setPhotoSrc] = useState(null);
  const [photoStatus, setPhotoStatus] = useState('idle');

  // How far the reader has turned the photo, in degrees clockwise. Every turn is drawn from the
  // original photo — never from the last result — so pressing Rotate four times gives back exactly
  // what was fetched instead of stacking lossy re-encodes on top of each other.
  const [photoRotation, setPhotoRotation] = useState(0);

  // The canvas result for the current turn, or null while the photo shows unturned (and also when
  // a turn could not be drawn, which falls back to the photo as fetched).
  const [rotatedPhotoSrc, setRotatedPhotoSrc] = useState(null);

  // Every open takes a ticket, and a fetch only writes back while its ticket is still the current
  // one. Closing the dialog — or opening another photo — invalidates the ticket, so a slow
  // response cannot land on a view it no longer belongs to.
  const photoRequestRef = useRef(0);

  // A turn takes a ticket for the same reason: the canvas draw is asynchronous, so a slow one must
  // not overwrite a later turn or land after the dialog has closed.
  const rotationRequestRef = useRef(0);

  // The relative labels would otherwise freeze at whatever the clock said on the first render,
  // so nudge a shared 'now' along every minute to keep them honest.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-refresh starts off, so the page never polls unless the reader asks it to.
  const [autoRefreshMs, setAutoRefreshMs] = useState(0);

  // Restore the remembered interval, but only when it matches an option this page actually
  // offers — a stale, corrupt or hand-edited value must never reach setInterval. Storage can
  // also be unavailable (private mode, blocked storage), which simply leaves auto-refresh off.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(AUTO_REFRESH_STORAGE_KEY);
      if (stored == null) return;

      const storedMs = Number(stored);
      if (AUTO_REFRESH_OPTIONS.some((option) => option.value === storedMs)) {
        setAutoRefreshMs(storedMs);
      }
    } catch {
      // No storage access: stay off.
    }
  }, []);

  const loadGames = useCallback(async () => {
    if (!eventId) return;

    setLoadingRequest(true);
    dispatch(getEvent(eventId));

    try {
      // The games slice does not track this request, so keep the spinner local to it. The photos
      // are left out: a row only needs to know whether one exists, and the viewer fetches the
      // single photo the reader asks to see.
      await dispatch(getGameByEventId({ eventId, includePhotos: false })).unwrap();
    } catch {
      // The slice stores the error; the page falls back to the empty state.
    } finally {
      setLoadingRequest(false);
    }
  }, [dispatch, eventId]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  // Re-arm the timer whenever the interval changes, or when loadGames itself does (a new event
  // id). Nothing is scheduled while auto-refresh is off. Ticks go through loadGames so they keep
  // the active filters, and a tick that lands while the previous request is still running skips
  // itself rather than stacking another request on top.
  useEffect(() => {
    if (autoRefreshMs <= 0) return undefined;

    const interval = setInterval(() => {
      if (loadingRef.current) return;
      loadGames();
    }, autoRefreshMs);

    return () => clearInterval(interval);
  }, [autoRefreshMs, loadGames]);

  const gameList = useMemo(() => (Array.isArray(games) ? games : []), [games]);
  const sections = event?.sections;

  // Resolve section ids to their name and draw order so the table reads like the draw.
  const sectionMeta = useMemo(() => {
    const meta = {};
    (Array.isArray(sections) ? sections : []).forEach((section, index) => {
      if (section?.id == null) return;
      meta[section.id] = {
        name: section.name || section.id,
        order: section.order != null ? section.order : index
      };
    });
    return meta;
  }, [sections]);

  const roundOptions = useMemo(() => getDistinctValues(gameList, 'round'), [gameList]);
  const greenOptions = useMemo(() => getDistinctValues(gameList, 'green'), [gameList]);

  const filteredGames = useMemo(() => {
    const term = filterName.trim().toLowerCase();

    return gameList.filter((game) => {
      if (term) {
        const searchText = [
          game?.teams?.teamA?.name,
          getPlayerNames(game?.teams?.teamA).join(' '),
          game?.teams?.teamB?.name,
          getPlayerNames(game?.teams?.teamB).join(' ')
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!searchText.includes(term)) return false;
      }

      if (filterRound !== '' && String(game?.round) !== String(filterRound)) return false;
      if (filterGreen !== '' && String(game?.green) !== String(filterGreen)) return false;
      if (filterStatus !== '' && normalizeStatus(game?.status) !== filterStatus) return false;

      return true;
    });
  }, [gameList, filterName, filterRound, filterGreen, filterStatus]);

  const sortedGames = useMemo(() => {
    // The order the list is drawn in, and the one it falls back to whenever no heading is active.
    const compareByDrawOrder = (a, b) => {
      const sectionA = sectionMeta[a?.section];
      const sectionB = sectionMeta[b?.section];

      if (sectionA && sectionB) {
        const orderDiff = compareValues(sectionA.order, sectionB.order);
        if (orderDiff !== 0) return orderDiff;
      }

      const roundDiff = compareValues(a?.round, b?.round);
      if (roundDiff !== 0) return roundDiff;

      const greenDiff = compareValues(a?.green, b?.green);
      if (greenDiff !== 0) return greenDiff;

      return compareValues(a?.rink, b?.rink);
    };

    return [...filteredGames].sort((a, b) => {
      if (!sortKey) return compareByDrawOrder(a, b);

      // Games the column cannot separate stay in the order they arrived in, which the stable sort
      // here preserves.
      const valuesA = getSortValues(a, sortKey, sectionMeta);
      const valuesB = getSortValues(b, sortKey, sectionMeta);

      for (let index = 0; index < valuesA.length; index += 1) {
        const diff = compareForKey(valuesA[index], valuesB[index], sortDirection);
        if (diff !== 0) return diff;
      }

      return 0;
    });
  }, [filteredGames, sectionMeta, sortKey, sortDirection]);

  // A heading walks its column asc -> desc -> off, and clicking a different heading starts that
  // column at asc. The click that turns the sort off — rather than starting a second lap — is what
  // puts the list back in draw order.
  const handleSortClick = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
      return;
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }

    setSortKey('');
    setSortDirection('asc');
  };

  const clearFilters = () => {
    setFilterName('');
    setFilterRound('');
    setFilterGreen('');
    setFilterStatus('');
  };

  const handleAutoRefreshChange = (e) => {
    const nextMs = Number(e.target.value);
    setAutoRefreshMs(nextMs);

    try {
      localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, String(nextMs));
    } catch {
      // Storage is unavailable: the choice still applies for this visit.
    }
  };

  const handleOpenGame = (game) => {
    if (!game?.id) return;
    navigate(`/game/scoring/${game.id}`);
  };

  const handleRowKeyDown = (e, game) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpenGame(game);
    }
  };

  // Open the viewer on a game and fetch that one game's photo. A failure and a missing photo read
  // the same to the reader — there is nothing to show — so both land on one friendly message.
  const openPhotoViewer = (game) => {
    const requestId = photoRequestRef.current + 1;
    photoRequestRef.current = requestId;

    // Every photo opens unturned, and any turn still being drawn for the previous one is dropped.
    rotationRequestRef.current += 1;
    setPhotoRotation(0);
    setRotatedPhotoSrc(null);

    setPhotoGame(game);
    setPhotoSrc(null);

    if (!game?.id) {
      setPhotoStatus('unavailable');
      return;
    }

    setPhotoStatus('loading');

    gameService
      .getById(game.id)
      .then((fullGame) => {
        if (photoRequestRef.current !== requestId) return;

        if (fullGame?.scorecardPhoto) {
          setPhotoSrc(fullGame.scorecardPhoto);
          setPhotoStatus('ready');
        } else {
          setPhotoStatus('unavailable');
        }
      })
      .catch(() => {
        if (photoRequestRef.current !== requestId) return;
        setPhotoStatus('unavailable');
      });
  };

  // The whole row is clickable, so the photo control has to keep its own events to itself
  // or the click would bubble up and navigate away to the scorecard as well.
  const handleViewPhoto = (e, game) => {
    e.stopPropagation();
    openPhotoViewer(game);
  };

  const handleViewPhotoKeyDown = (e) => {
    // Enter and Space would otherwise reach the row and open the scorecard scoring page.
    e.stopPropagation();
  };

  // Closing drops the fetched photo rather than holding the base64 string for the rest of the
  // visit, and invalidates any fetch still in flight so it cannot write back after the fact.
  const handleClosePhoto = () => {
    photoRequestRef.current += 1;
    rotationRequestRef.current += 1;
    setPhotoGame(null);
    setPhotoSrc(null);
    setPhotoStatus('idle');
    setPhotoRotation(0);
    setRotatedPhotoSrc(null);
  };

  // Each press turns the photo a further 90° clockwise, 270° round to 0° again. The draw always
  // starts from the fetched photo at the new angle, so the turn never compounds.
  const handleRotatePhoto = () => {
    const nextRotation = (photoRotation + 90) % 360;
    setPhotoRotation(nextRotation);

    const requestId = rotationRequestRef.current + 1;
    rotationRequestRef.current = requestId;

    if (nextRotation === 0) {
      // Back to the start: show — and download — the fetched photo itself, untouched.
      setRotatedPhotoSrc(null);
      return;
    }

    rotatePhoto(photoSrc, nextRotation).then((result) => {
      // A newer press, or a closed dialog, has already moved on; this result is stale.
      if (rotationRequestRef.current !== requestId) return;
      setRotatedPhotoSrc(result);
    });
  };

  // What the viewer shows, and so what Download saves: the fetched photo itself while it is
  // unturned, and the canvas result once it is turned. A turn that could not be drawn falls back to
  // the photo as fetched rather than showing nothing.
  const displayedPhotoSrc = photoRotation === 0 || !rotatedPhotoSrc ? photoSrc : rotatedPhotoSrc;

  // The reader turns a sideways scorecard to make it readable, so the file they keep is the one
  // they are looking at: unturned saves the fetched bytes, turned saves the canvas bytes shown.
  const handleDownloadPhoto = () => {
    if (!displayedPhotoSrc) return;

    let link = null;

    try {
      link = document.createElement('a');
      link.href = displayedPhotoSrc;
      link.download = getPhotoFileName(photoGame, displayedPhotoSrc);
      // The anchor has to be in the document for the click to count, and goes straight back out.
      document.body.appendChild(link);
      link.click();
    } catch {
      // A browser that refuses the download leaves the dialog exactly as it was.
    } finally {
      // Already detached when the append never happened; removing is safe either way.
      if (link) link.remove();
    }
  };

  const loading = Boolean(loadingEvent || loadingRequest);

  return (
    <Box p={3}>
      <Box mb={2}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/events/${eventId}`)}>
          Back to Event
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>
        {event?.name ? `${event.name} — All Games` : 'All Games'}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Every game in this event on one page. Filter by team or player name, round, green or
        status, then click a row to open its scorecard.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="Team or player"
            placeholder="Filter by team or player name"
            size="small"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            sx={{ minWidth: { xs: '100%', md: 260 } }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 160 } }}>
            <InputLabel id="all-games-round-label">Round</InputLabel>
            <Select
              labelId="all-games-round-label"
              label="Round"
              value={filterRound}
              onChange={(e) => setFilterRound(e.target.value)}>
              <MenuItem value={''}>All rounds</MenuItem>
              {roundOptions.map((round) => (
                <MenuItem key={round} value={round}>
                  Round {round}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 160 } }}>
            <InputLabel id="all-games-green-label">Green</InputLabel>
            <Select
              labelId="all-games-green-label"
              label="Green"
              value={filterGreen}
              onChange={(e) => setFilterGreen(e.target.value)}>
              <MenuItem value={''}>All greens</MenuItem>
              {greenOptions.map((green) => (
                <MenuItem key={green} value={green}>
                  Green {green}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 160 } }}>
            <InputLabel id="all-games-status-label">Status</InputLabel>
            <Select
              labelId="all-games-status-label"
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value={''}>All statuses</MenuItem>
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {STATUS_META[status].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            sx={{ whiteSpace: 'nowrap' }}>
            Clear filters
          </Button>

          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={loadGames}
            disabled={loadingRequest}
            sx={{ whiteSpace: 'nowrap' }}>
            Refresh
          </Button>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 160 } }}>
            <InputLabel id="all-games-auto-refresh-label">Auto-refresh</InputLabel>
            <Select
              labelId="all-games-auto-refresh-label"
              label="Auto-refresh"
              value={autoRefreshMs}
              onChange={handleAutoRefreshChange}>
              {AUTO_REFRESH_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {sortedGames.length} of {gameList.length} games
      </Typography>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="240px"
          flexDirection="column"
          gap={2}>
          <CircularProgress size={60} />
          <Typography variant="body2" color="text.secondary">
            Loading games...
          </Typography>
        </Box>
      ) : sortedGames.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No games match your filters.
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            border: `1px solid ${theme.palette.divider}`
          }}>
          {/* The list is as long as it needs to be and the page scrolls, so the header is left
              unsticky: pinned to top: 0 it would sit behind the fixed AppBar while scrolling. */}
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ minWidth: 160 }}>
                  <TableSortLabel
                    component="span"
                    active={sortKey === 'section'}
                    direction={sortDirection}
                    onClick={() => handleSortClick('section')}>
                    Section
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ minWidth: 80 }}>
                  <TableSortLabel
                    component="span"
                    active={sortKey === 'round'}
                    direction={sortDirection}
                    onClick={() => handleSortClick('round')}>
                    Round
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ minWidth: 160 }}>
                  <TableSortLabel
                    component="span"
                    active={sortKey === 'greenRink'}
                    direction={sortDirection}
                    onClick={() => handleSortClick('greenRink')}>
                    Green / Rink
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell sx={{ minWidth: 200 }}>
                  <TableSortLabel
                    component="span"
                    active={sortKey === 'teamA'}
                    direction={sortDirection}
                    onClick={() => handleSortClick('teamA')}>
                    Team A
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell sx={{ minWidth: 200 }}>
                  <TableSortLabel
                    component="span"
                    active={sortKey === 'teamB'}
                    direction={sortDirection}
                    onClick={() => handleSortClick('teamB')}>
                    Team B
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ minWidth: 120 }}>
                  <TableSortLabel
                    component="span"
                    active={sortKey === 'status'}
                    direction={sortDirection}
                    onClick={() => handleSortClick('status')}>
                    Status
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ minWidth: 140 }}>
                  <TableSortLabel
                    component="span"
                    active={sortKey === 'lastUpdate'}
                    direction={sortDirection}
                    onClick={() => handleSortClick('lastUpdate')}>
                    Last update
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ minWidth: 110 }}>
                  <TableSortLabel
                    component="span"
                    active={sortKey === 'photo'}
                    direction={sortDirection}
                    onClick={() => handleSortClick('photo')}>
                    Photo
                  </TableSortLabel>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedGames.map((game) => {
                const status = normalizeStatus(game?.status);
                const statusMeta = status ? STATUS_META[status] : null;
                const teamA = game?.teams?.teamA;
                const teamB = game?.teams?.teamB;
                const playersA = getPlayerNames(teamA);
                const playersB = getPlayerNames(teamB);
                const updatedAtMs = getUpdatedAtMs(game?.updatedAt);

                return (
                  <Tooltip key={game.id} title="Open game scoring" placement="top" enterDelay={600}>
                    <ClickableTableRow
                      tabIndex={0}
                      onClick={() => handleOpenGame(game)}
                      onKeyDown={(e) => handleRowKeyDown(e, game)}>
                      <StyledTableCell>
                        <Typography variant="body2">
                          {sectionMeta[game?.section]?.name || game?.section || '—'}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography variant="body2">{game?.round ?? '—'}</Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography variant="body2">{getGreenRinkLabel(game)}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {getTeamLabel(teamA)}
                        </Typography>
                        {playersA.length > 0 && teamA?.name ? (
                          <Typography variant="caption" color="text.secondary">
                            {playersA.join(', ')}
                          </Typography>
                        ) : null}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {getTeamLabel(teamB)}
                        </Typography>
                        {playersB.length > 0 && teamB?.name ? (
                          <Typography variant="caption" color="text.secondary">
                            {playersB.join(', ')}
                          </Typography>
                        ) : null}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {statusMeta ? (
                          <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
                        ) : (
                          <Typography variant="body2">—</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {updatedAtMs ? (
                          <Tooltip
                            title={formatExactTime(updatedAtMs)}
                            placement="top"
                            enterDelay={600}>
                            <Typography variant="body2">
                              {formatRelativeTime(updatedAtMs, now)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2">—</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {hasPhoto(game) ? (
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<PhotoCameraIcon />}
                            onClick={(e) => handleViewPhoto(e, game)}
                            onKeyDown={handleViewPhotoKeyDown}
                            sx={{ whiteSpace: 'nowrap' }}>
                            View
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </StyledTableCell>
                    </ClickableTableRow>
                  </Tooltip>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* The row only knows that a photo exists, so the dialog fetches the one game it shows and
          drops the result when it closes — the base64 string never reaches the table. */}
      <Dialog open={Boolean(photoGame)} onClose={handleClosePhoto} maxWidth="lg" fullWidth>
        <DialogTitle>{`${getGameLabel(photoGame)} — Scorecard photo`}</DialogTitle>
        <DialogContent>
          {photoStatus === 'loading' ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="240px"
              flexDirection="column"
              gap={2}>
              <CircularProgress size={60} />
              <Typography variant="body2" color="text.secondary">
                Loading scorecard photo...
              </Typography>
            </Box>
          ) : photoStatus === 'ready' ? (
            <Box
              component="img"
              src={displayedPhotoSrc}
              alt="Scorecard photo"
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                mx: 'auto'
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No scorecard photo available for this game.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<RotateRightIcon />}
            onClick={handleRotatePhoto}
            disabled={photoStatus !== 'ready'}>
            Rotate
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPhoto}
            disabled={photoStatus !== 'ready'}>
            Download
          </Button>
          <Button onClick={handleClosePhoto}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventAllGames;
